'use client'

import { formatDuration, formatPrice } from '@/lib/c2c/format'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import { DurationConnector } from './duration-connector'
import { BusIcon } from './icons/bus-icon'
import { StarRating } from './star-rating'

interface TripCardProps {
    departTime: string
    arriveTime: string
    departStation: string
    arriveStation: string
    duration: number
    price?: number
    carrierName: string
    carrierLogo?: string
    carrierRating?: number
    isLuxury?: boolean
    searchUrl: string
    transfers?: number
    mode: string
}

const tooltipContentClass = 'rounded-none bg-[#2e3033] px-5 py-4 text-sm text-white'

export function TripCard({
    departTime,
    arriveTime,
    departStation,
    arriveStation,
    duration,
    price,
    carrierName,
    carrierLogo,
    carrierRating,
    isLuxury = false,
    searchUrl,
    transfers = 0,
    mode,
}: TripCardProps) {
    const formattedDuration = formatDuration(duration)
    const modeLabel = mode.charAt(0).toUpperCase() + mode.slice(1)

    return (
        <button
            type="button"
            onClick={() => window.location.assign(searchUrl)}
            className="block w-full cursor-pointer rounded-[7px] border border-[#e2e2e2] bg-white p-4 text-left transition-shadow hover:shadow-md md:px-6 md:py-5"
            data-testid="trip-card"
        >
            {/* Row 1: Carrier info + rating + price */}
            <div className="mb-3 flex items-center gap-2 md:mb-4">
                {/* Mobile: bus icon + carrier name text */}
                <span className="md:hidden">
                    <BusIcon className="h-4 w-4 shrink-0 text-[#878787]" />
                </span>
                {carrierLogo ? (
                    <>
                        <span className="truncate text-sm text-[#767676] uppercase md:hidden">
                            {carrierName}
                        </span>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={carrierLogo}
                                    alt={carrierName}
                                    className="hidden h-5 max-w-[80px] object-contain md:block"
                                />
                            </TooltipTrigger>
                            <TooltipContent side="top" className={tooltipContentClass}>
                                {carrierName} ({modeLabel})
                            </TooltipContent>
                        </Tooltip>
                    </>
                ) : (
                    <span className="truncate text-sm text-[#767676] uppercase">{carrierName}</span>
                )}
                {carrierRating != null && <StarRating rating={carrierRating} className="md:ml-1" />}
                {price != null && (
                    <span className="ml-auto shrink-0 text-lg font-bold text-[#007380] md:text-xl md:text-[#44474a]">
                        {formatPrice(price)}
                    </span>
                )}
            </div>

            {/* Row 2: Times + duration */}
            <div className="flex items-start">
                {/* Bus circle icon — desktop only */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="mr-4 hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-white bg-[#ebeef3] md:flex">
                            <BusIcon className="w-[26px] text-[#44474a]" />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className={tooltipContentClass}>
                        {modeLabel}
                    </TooltipContent>
                </Tooltip>

                <div className="flex min-w-0 flex-1 items-baseline gap-1 md:items-start md:gap-0">
                    {/* Departure */}
                    <div className="shrink-0 md:min-w-0 md:flex-[2]">
                        <p className="text-lg font-bold text-[#2e3033] md:text-xl">{departTime}</p>
                        <p className="hidden truncate text-sm leading-[21px] text-[#2e3033] md:block">
                            {departStation}
                        </p>
                    </div>

                    {/* Duration — mobile: inline text, desktop: connector line */}
                    <span className="text-xs text-[#767676] italic md:hidden">
                        {formattedDuration}
                    </span>
                    <div className="hidden md:flex md:flex-1 md:items-center md:px-3">
                        <DurationConnector duration={formattedDuration} />
                    </div>

                    {/* Arrival */}
                    <div className="shrink-0 md:min-w-0 md:flex-[2]">
                        <p className="text-lg font-bold text-[#2e3033] md:text-xl">{arriveTime}</p>
                        <p className="hidden truncate text-sm leading-[21px] text-[#2e3033] md:block">
                            {arriveStation}
                        </p>
                    </div>
                </div>
            </div>

            {/* Row 3: Stations — mobile only (desktop shows them under each time above) */}
            <div className="flex items-center gap-4 text-sm text-[#2e3033] md:hidden">
                <span className="truncate">{departStation}</span>
                <span className="truncate">{arriveStation}</span>
            </div>

            {/* Transfers indicator */}
            {transfers > 0 && (
                <p className="mt-2 text-xs text-[#767676] md:pl-[60px]">
                    {transfers} transfer{transfers > 1 ? 's' : ''}
                </p>
            )}

            {/* Luxury badge */}
            {isLuxury && (
                <div className="mt-2 inline-block rounded-2xl bg-[#e2d3fa] px-3 text-sm text-[#6341b4]">
                    Luxury
                </div>
            )}
        </button>
    )
}
